import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography, Button, TextField, Box,
  Avatar, CircularProgress, Alert, Popper, Grow, Paper,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  ClickAwayListener, Rating, IconButton
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import PhoneIcon from '@mui/icons-material/Phone';
import VideocamIcon from '@mui/icons-material/Videocam';
import PersonIcon from '@mui/icons-material/Person';
import { Search as SearchIcon } from '@mui/icons-material';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { useChat } from '../../contexts/ChatContext';
import { useCall } from '../../contexts/CallContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import ConnectingScreen from '../consultation/ConnectingScreen';
import CallConsentModal from '../consultation/CallConsentModal';

/* ───────── animations ───────── */
const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;
const slideUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;
const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 8px rgba(76,175,80,0.4); }
  50% { box-shadow: 0 0 18px rgba(76,175,80,0.8); }
`;

const AvatarRing = styled.div`
  width: 60px;
  height: 75px;
  border-radius: 8px;
  border: 3px solid ${p => p.ringColor || '#ccc'};
  padding: 2px;
  overflow: hidden;
  animation: ${p => p.online ? pulseGlow : 'none'} 2s ease-in-out infinite;
`;

/* ───────── styled components ───────── */
const Page = styled.div`
  min-height: calc(100vh - 160px);
  background: #FFFDF7;
  padding: 2rem 1.5rem 3rem;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  text-align: center;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(1.5rem, 4vw, 2.2rem);
  font-weight: 800;
  margin: 0 0 0.5rem;
  color: #2C1810;
`;

const PageSub = styled.p`
  text-align: center;
  color: #9B8B7A;
  font-size: 0.9rem;
  margin: 0 0 1.5rem;
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 1rem;
  animation: ${slideUp} 0.6s ease-out;
`;

const FilterChip = styled.button`
  padding: 6px 18px;
  border-radius: 8px;
  border: 1px solid ${p => p.active ? '#DAA520' : 'rgba(218,165,32,0.15)'};
  background: ${p => p.active ? 'rgba(218,165,32,0.1)' : '#FFFFFF'};
  color: ${p => p.active ? '#8B6914' : '#9B8B7A'};
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    border-color: #DAA520;
    color: #8B6914;
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
  animation: ${slideUp} 0.8s ease-out 0.2s both;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

/* ───────── status helpers ───────── */
const STATUS_RING = {
  online: '#4caf50',
  busy: '#ff9800',
  offline: '#ccc',
};

const FILTERS = ['All', 'Online', 'Vedic Astrology', 'Numerology', 'Tarot Reading', 'Palmistry'];

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1].slice(-1) : '';
  return (first + last).toUpperCase();
};

/* ───────── astrologer card component ───────── */
const AstrologerCard = ({ astrologer, onlineStatus, onClick, selected }) => {
  const name = astrologer.name || astrologer.user?.name || 'Astrologer';
  const initials = getInitials(name);
  const ringColor = STATUS_RING[onlineStatus] || STATUS_RING.offline;
  const isOnline = onlineStatus === 'online';
  const expertiseTags = astrologer.expertise
    ? astrologer.expertise.split(',').filter(Boolean).slice(0, 3)
    : [];
  const languages = astrologer.languages
    ? astrologer.languages.split(',').filter(Boolean)
    : [];
  const callRate = astrologer.price_per_minute_call || 15;

  return (
    <Paper
      onClick={onClick}
      elevation={0}
      sx={{
        display: 'flex', alignItems: 'stretch',
        cursor: 'pointer', borderRadius: 3,
        userSelect: 'none', overflow: 'hidden',
        background: '#FFFFFF',
        border: selected
          ? '1px solid #DAA520'
          : '1px solid rgba(218,165,32,0.1)',
        borderLeft: `4px solid ${ringColor}`,
        transition: 'all 0.3s cubic-bezier(.25,.8,.25,1)',
        transform: selected ? 'scale(1.02)' : 'scale(1)',
        '&:hover': {
          transform: 'scale(1.02)',
          borderColor: 'rgba(218,165,32,0.3)',
          boxShadow: '0 8px 30px rgba(218,165,32,0.1)',
        },
      }}
    >
      {/* Left: Avatar + Experience */}
      <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', p: 1.5, minWidth: 90,
      }}>
        <AvatarRing ringColor={ringColor} online={isOnline}>
          <Avatar
            variant="rounded"
            src={astrologer.profile_image || undefined}
            sx={{
              width: '100%', height: '100%', borderRadius: '6px',
              bgcolor: '#DAA520', fontSize: 18, fontWeight: 700,
            }}
          >
            {initials}
          </Avatar>
        </AvatarRing>
        {astrologer.experience_years > 0 && (
          <Typography variant="caption" sx={{ mt: 0.5, fontSize: 10, fontWeight: 500, color: '#9B8B7A' }}>
            {astrologer.experience_years} yrs exp
          </Typography>
        )}
      </Box>

      {/* Right: Name, Expertise, Languages, Rate */}
      <Box sx={{
        flex: 1, display: 'flex', flexDirection: 'column',
        justifyContent: 'center', py: 1.5, pr: 1.5,
      }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2, color: '#2C1810' }}>
          {name}
        </Typography>
        {expertiseTags.length > 0 && (
          <Typography variant="caption" sx={{ fontSize: 11, mt: 0.25, color: '#9B8B7A' }}>
            {expertiseTags.join(' · ')}
          </Typography>
        )}
        {languages.length > 0 && (
          <Typography variant="caption" sx={{ fontSize: 10, mt: 0.25, color: '#B8A898' }}>
            {languages.join(', ')}
          </Typography>
        )}
        <Typography variant="caption" sx={{ mt: 0.5, fontWeight: 600, color: '#4caf50', fontSize: 12 }}>
          ₹{callRate}/min
        </Typography>
      </Box>
    </Paper>
  );
};

/* ───────── main component ───────── */
const TalkToAstrologerEnhanced = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { socket, connectGlobal } = useChat();
  const { startCall } = useCall();

  const [astrologers, setAstrologers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  // Popup state
  const [selectedId, setSelectedId] = useState(null);
  const anchorRefs = useRef({});

  // On-demand state
  const [connecting, setConnecting] = useState(null);
  const [insufficientBalance, setInsufficientBalance] = useState(null);

  // DPDPA consent state
  const [callConsent, setCallConsent] = useState(null);
  const [consentModalOpen, setConsentModalOpen] = useState(false);
  const [pendingConsult, setPendingConsult] = useState(null);
  const [jitsiUnavailable, setJitsiUnavailable] = useState(false);

  // Birth details modal (pre-call validation)
  const [birthDetailsModal, setBirthDetailsModal] = useState({ open: false, astrologer: null, type: null, existingProfile: null });
  const [jitsiFallbackAstrologer, setJitsiFallbackAstrologer] = useState(null);

  const fetchAstrologers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/astrologers?limit=100');
      const data = response.data?.astrologers || response.data || [];
      setAstrologers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load astrologers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAstrologers(); }, [fetchAstrologers]);

  useEffect(() => {
    if (!isAuthenticated) return;
    api.get('/profile/call-consent')
      .then(resp => setCallConsent(resp.data?.consented === true))
      .catch(() => setCallConsent(false));
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    connectGlobal(user.id, 'user');
  }, [isAuthenticated, user, connectGlobal]);

  useEffect(() => {
    if (!socket) return;
    const handler = ({ astrologerId, status }) => {
      setAstrologers(prev =>
        prev.map(a =>
          a.id === astrologerId
            ? { ...a, online_status: status, is_online: status === 'online' || status === 'busy' }
            : a
        )
      );
    };
    socket.on('astrologer-status-change', handler);
    return () => socket.off('astrologer-status-change', handler);
  }, [socket]);

  // Ref to hold connecting state for async socket callback (avoids stale closure)
  const connectingRef = useRef(null);
  useEffect(() => { connectingRef.current = connecting; }, [connecting]);

  useEffect(() => {
    if (!socket) return;
    const handler = ({ consultationId, chatRoomId, type }) => {
      // Read rate from ref (avoids stale closure on connecting state)
      const rate = connectingRef.current?.ratePerMin || 0;
      setConnecting(null);
      // Start call globally — PiP appears via GlobalCallPiP in App.js
      // User stays on current page (no navigation to chat page)
      startCall(consultationId, type, {
        chatRoomId,
        displayName: user?.name || 'User',
        ratePerMin: rate,
      });
    };
    const rejectHandler = () => {
      setConnecting(null);
      setError('Astrologer is unavailable. Please try another or schedule a slot.');
    };
    socket.on('call-accepted', handler);
    socket.on('call-rejected', rejectHandler);
    return () => {
      socket.off('call-accepted', handler);
      socket.off('call-rejected', rejectHandler);
    };
  }, [socket, navigate]);

  const handleInstantConsult = async (astrologer, type) => {
    if (!isAuthenticated) { navigate('/login'); return; }

    // Validate user has DOB, time, and place before allowing call
    try {
      const profileRes = await api.get('/profile');
      const p = profileRes.data?.user || profileRes.data;
      const hasDob = p.date_of_birth && p.date_of_birth !== '0001-01-01T00:00:00Z';
      const hasTime = p.time_of_birth && p.time_of_birth !== '0001-01-01T00:00:00Z';
      const hasPlace = p.place_of_birth && p.place_of_birth.trim() !== '';
      if (!hasDob || !hasTime || !hasPlace) {
        setBirthDetailsModal({ open: true, astrologer, type, existingProfile: p });
        return;
      }
    } catch {
      setError('Unable to verify your profile. Please try again.');
      return;
    }

    if ((type === 'call' || type === 'video') && !callConsent) {
      setPendingConsult({ astrologer, type });
      setConsentModalOpen(true);
      return;
    }

    setSelectedId(null);
    try {
      const resp = await api.post('/consultations/instant', {
        astrologer_id: astrologer.id,
        type,
      });
      setConnecting({
        astrologerName: astrologer.name || astrologer.user?.name,
        type,
        consultationId: resp.data.consultation_id,
        ratePerMin: getRate(astrologer, type),
      });
    } catch (err) {
      if (err.response?.status === 503) {
        setJitsiFallbackAstrologer(astrologer);
        setJitsiUnavailable(true);
      } else if (err.response?.data?.error === 'insufficient_balance') {
        setInsufficientBalance({
          required: err.response.data.required,
          available: err.response.data.available,
        });
      } else {
        setError(err.response?.data?.message || err.response?.data?.error || 'Failed to connect');
      }
    }
  };

  const handleConsentGranted = () => {
    setCallConsent(true);
    setConsentModalOpen(false);
    if (pendingConsult) {
      const { astrologer, type } = pendingConsult;
      setPendingConsult(null);
      handleInstantConsult(astrologer, type);
    }
  };

  const handleConsentDeclined = () => {
    setConsentModalOpen(false);
    setPendingConsult(null);
  };

  const handleCancelConnect = async () => {
    if (connecting?.consultationId) {
      try { await api.post(`/consultations/${connecting.consultationId}/reject`); } catch (_) {}
    }
    setConnecting(null);
  };

  const handleThumbClick = (astrologer, e) => {
    anchorRefs.current[astrologer.id] = e.currentTarget;
    setSelectedId(prev => prev === astrologer.id ? null : astrologer.id);
  };

  const getRate = (astrologer, type) => {
    if (type === 'chat') return astrologer.price_per_minute_chat || 10;
    if (type === 'call') return astrologer.price_per_minute_call || 20;
    if (type === 'video') return astrologer.price_per_minute_video || 30;
    return 10;
  };

  const filteredAstrologers = astrologers.filter(a => {
    const name = a.name || a.user?.name || '';
    const spec = a.specialization || '';
    const matchSearch = name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      spec.toLowerCase().includes(searchTerm.toLowerCase());
    const onlineStatus = a.online_status || (a.is_online ? 'online' : 'offline');
    const matchFilter =
      activeFilter === 'All' ||
      (activeFilter === 'Online' && onlineStatus === 'online') ||
      spec === activeFilter;
    return matchSearch && matchFilter;
  });

  if (connecting) {
    return (
      <Page>
        <Content>
          <Box sx={{ maxWidth: 500, mx: 'auto', mt: 4 }}>
            <ConnectingScreen
              astrologerName={connecting.astrologerName}
              type={connecting.type}
              onTimeout={handleCancelConnect}
              onCancel={handleCancelConnect}
            />
          </Box>
        </Content>
      </Page>
    );
  }

  return (
    <Page>
      <Content>
        <PageTitle>Talk to an Astrologer</PageTitle>
        <PageSub>Chat, voice call, or video call with our expert astrologers instantly</PageSub>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <FiltersRow>
          {FILTERS.map(f => (
            <FilterChip
              key={f}
              active={activeFilter === f}
              onClick={() => setActiveFilter(f)}
            >
              {f === 'Online' ? '🟢 Online Now' : f}
            </FilterChip>
          ))}
        </FiltersRow>

        <Box sx={{ maxWidth: 500, mx: 'auto', mb: 2 }}>
          <TextField
            fullWidth size="small"
            placeholder="Search by name or specialization..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: '#9B8B7A' }} />,
              sx: {
                color: '#2C1810',
                background: '#FFFFFF',
                borderRadius: 2,
                '& fieldset': { borderColor: 'rgba(218,165,32,0.15)' },
                '&:hover fieldset': { borderColor: 'rgba(218,165,32,0.3) !important' },
                '&.Mui-focused fieldset': { borderColor: '#DAA520 !important' },
              }
            }}
          />
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress sx={{ color: '#DAA520' }} /></Box>
        ) : (
          <ClickAwayListener onClickAway={() => setSelectedId(null)}>
            <CardsGrid>
              {filteredAstrologers.map(astrologer => {
                const onlineStatus = astrologer.online_status || (astrologer.is_online ? 'online' : 'offline');
                const isOpen = selectedId === astrologer.id;
                const name = astrologer.name || astrologer.user?.name || 'Astrologer';
                const isOnline = onlineStatus === 'online';
                const chatRate = getRate(astrologer, 'chat');

                return (
                  <Box key={astrologer.id} sx={{ position: 'relative' }}>
                    <AstrologerCard
                      astrologer={astrologer}
                      onlineStatus={onlineStatus}
                      onClick={(e) => handleThumbClick(astrologer, e)}
                      selected={isOpen}
                    />

                    {/* Popup */}
                    <Popper
                      open={isOpen}
                      anchorEl={anchorRefs.current[astrologer.id]}
                      placement="bottom"
                      transition
                      sx={{ zIndex: 1300 }}
                    >
                      {({ TransitionProps }) => (
                        <Grow {...TransitionProps} timeout={150}>
                          <Paper
                            elevation={8}
                            sx={{
                              mt: 1, p: 1.5, borderRadius: 3, minWidth: 200,
                              textAlign: 'center',
                              background: '#FFFFFF',
                              border: '1px solid rgba(218,165,32,0.2)',
                              boxShadow: '0 8px 32px rgba(139,105,20,0.12)',
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ mb: 0.3, color: '#2C1810', fontWeight: 700 }}>{name}</Typography>
                            <Typography variant="caption" sx={{ color: '#9B8B7A', display: 'block', mb: 0.3 }}>
                              {astrologer.specialization || ''}
                            </Typography>
                            {astrologer.rating > 0 && (
                              <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} mb={0.5}>
                                <Rating value={astrologer.rating} readOnly size="small" />
                                <Typography variant="caption" sx={{ color: '#DAA520' }}>({astrologer.rating})</Typography>
                              </Box>
                            )}
                            <Typography variant="caption" sx={{ color: '#9B8B7A', display: 'block', mb: 1 }}>
                              From ₹{chatRate}/min
                            </Typography>

                            <Box display="flex" justifyContent="center" gap={1}>
                              <IconButton
                                size="small"
                                sx={{
                                  bgcolor: isOnline ? '#1976d2' : 'rgba(0,0,0,0.06)',
                                  color: isOnline ? '#fff' : '#ccc',
                                  width: 40, height: 40,
                                  '&:hover': { bgcolor: isOnline ? '#1565c0' : undefined },
                                }}
                                disabled={!isOnline}
                                onClick={() => handleInstantConsult(astrologer, 'chat')}
                                title="Chat"
                              >
                                <ChatIcon fontSize="small" />
                              </IconButton>

                              <IconButton
                                size="small"
                                sx={{
                                  bgcolor: isOnline ? '#2e7d32' : 'rgba(0,0,0,0.06)',
                                  color: isOnline ? '#fff' : '#ccc',
                                  width: 40, height: 40,
                                  '&:hover': { bgcolor: isOnline ? '#1b5e20' : undefined },
                                }}
                                disabled={!isOnline}
                                onClick={() => handleInstantConsult(astrologer, 'call')}
                                title="Voice Call"
                              >
                                <PhoneIcon fontSize="small" />
                              </IconButton>

                              <IconButton
                                size="small"
                                sx={{
                                  bgcolor: isOnline ? '#B8860B' : 'rgba(0,0,0,0.06)',
                                  color: isOnline ? '#fff' : '#ccc',
                                  width: 40, height: 40,
                                  '&:hover': { bgcolor: isOnline ? '#8B6914' : undefined },
                                }}
                                disabled={!isOnline}
                                onClick={() => handleInstantConsult(astrologer, 'video')}
                                title="Video Call"
                              >
                                <VideocamIcon fontSize="small" />
                              </IconButton>

                              <IconButton
                                size="small"
                                sx={{
                                  bgcolor: 'rgba(218,165,32,0.08)',
                                  color: '#8B6914',
                                  width: 40, height: 40,
                                  '&:hover': { bgcolor: 'rgba(218,165,32,0.15)' },
                                }}
                                onClick={() => {
                                  setSelectedId(null);
                                  navigate(`/astrologer/${astrologer.id}`);
                                }}
                                title="View Profile"
                              >
                                <PersonIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Paper>
                        </Grow>
                      )}
                    </Popper>
                  </Box>
                );
              })}

              {filteredAstrologers.length === 0 && !loading && (
                <Box textAlign="center" py={4} width="100%" gridColumn="1 / -1">
                  <Typography sx={{ color: '#9B8B7A' }}>
                    No astrologers found matching your criteria
                  </Typography>
                </Box>
              )}
            </CardsGrid>
          </ClickAwayListener>
        )}

        {/* DPDPA consent modal for voice/video calls */}
        <CallConsentModal
          open={consentModalOpen}
          onConsent={handleConsentGranted}
          onDecline={handleConsentDeclined}
        />

        {/* Insufficient balance dialog */}
        <Dialog
          open={!!insufficientBalance}
          onClose={() => setInsufficientBalance(null)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              background: '#FFFFFF',
              border: '1px solid rgba(218,165,32,0.2)',
              borderRadius: 3,
            }
          }}
        >
          <DialogTitle sx={{ color: '#c0392b', fontFamily: '"Playfair Display", Georgia, serif' }}>Insufficient Balance</DialogTitle>
          <DialogContent>
            <Typography sx={{ color: '#2C1810' }}>
              You need ₹{insufficientBalance?.required} but have ₹{insufficientBalance?.available}.
            </Typography>
            <Typography variant="body2" sx={{ color: '#9B8B7A', mt: 1 }}>
              Please recharge your wallet to start a consultation.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInsufficientBalance(null)} sx={{ color: '#9B8B7A' }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => { setInsufficientBalance(null); navigate('/wallet'); }}
              sx={{ background: 'linear-gradient(135deg, #DAA520, #B8860B)', '&:hover': { background: 'linear-gradient(135deg, #FFD700, #DAA520)' } }}
            >
              Recharge Now
            </Button>
          </DialogActions>
        </Dialog>

        {/* Jitsi unavailable fallback dialog */}
        <Dialog
          open={jitsiUnavailable}
          onClose={() => setJitsiUnavailable(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              background: '#FFFFFF',
              border: '1px solid rgba(218,165,32,0.2)',
              borderRadius: 3,
            }
          }}
        >
          <DialogTitle sx={{ color: '#e67e22', fontFamily: '"Playfair Display", Georgia, serif' }}>Video Calls Temporarily Unavailable</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: '#6B5744' }}>
              Our video call service is currently experiencing issues.
              You can try again later or start a chat consultation instead.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setJitsiUnavailable(false)} sx={{ color: '#9B8B7A' }}>
              Try Later
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setJitsiUnavailable(false);
                if (jitsiFallbackAstrologer) {
                  handleInstantConsult(jitsiFallbackAstrologer, 'chat');
                }
              }}
              sx={{
                background: 'linear-gradient(135deg, #DAA520, #B8860B)',
                '&:hover': { background: 'linear-gradient(135deg, #FFD700, #DAA520)' },
              }}
            >
              Start Chat Instead
            </Button>
          </DialogActions>
        </Dialog>

        {/* Birth Details Modal — shown when user tries to call without DOB/time/place */}
        <Dialog
          open={birthDetailsModal.open}
          onClose={() => setBirthDetailsModal({ open: false })}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              background: '#FFFFFF',
              border: '1px solid rgba(218,165,32,0.2)',
              borderRadius: 3,
            }
          }}
        >
          <DialogTitle sx={{ color: '#333', fontFamily: '"Playfair Display", Georgia, serif' }}>
            Complete Your Birth Details
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: '#6B5744', mb: 2 }}>
              For the most accurate consultation, please provide your birth details. This helps the astrologer prepare your Kundli and personalized readings.
            </DialogContentText>
            <Box component="form" id="birth-details-form" onSubmit={async (e) => {
              e.preventDefault();
              const fd = new FormData(e.target);
              const dob = fd.get('dob');
              const tob = fd.get('tob');
              const place = fd.get('place');
              if (!dob || !tob || !place) return;
              try {
                await api.put('/profile', {
                  date_of_birth: dob,
                  time_of_birth: tob,
                  place_of_birth: place,
                });
                const { astrologer, type } = birthDetailsModal;
                setBirthDetailsModal({ open: false });
                if (astrologer && type) handleInstantConsult(astrologer, type);
              } catch {
                setError('Failed to save birth details. Please try again.');
              }
            }} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                name="dob"
                label="Date of Birth"
                type="date"
                defaultValue={birthDetailsModal.existingProfile?.date_of_birth?.split('T')[0] || ''}
                InputLabelProps={{ shrink: true }}
                required
                fullWidth
              />
              <TextField
                name="tob"
                label="Time of Birth"
                type="time"
                defaultValue=""
                InputLabelProps={{ shrink: true }}
                required
                fullWidth
              />
              <TextField
                name="place"
                label="Place of Birth"
                defaultValue={birthDetailsModal.existingProfile?.place_of_birth || ''}
                placeholder="e.g., Chennai, Madurai"
                required
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBirthDetailsModal({ open: false })} sx={{ color: '#9B8B7A' }}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="birth-details-form"
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #DAA520, #B8860B)',
                '&:hover': { background: 'linear-gradient(135deg, #FFD700, #DAA520)' },
              }}
            >
              Save & Continue Call
            </Button>
          </DialogActions>
        </Dialog>
      </Content>
    </Page>
  );
};

export default TalkToAstrologerEnhanced;
